import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { translations, mockPhotos } from '../data/mock';
import { CatCard } from '../components/CatCard';
import { Award, Camera, Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { catsAPI, awardsAPI, instagramAPI } from '../services/api';
import { Footer } from '../components/Footer';

export const OurCats = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Get tab from URL params
  const urlTab = searchParams.get('tab');
  
  const [activeTab, setActiveTab] = useState(urlTab || 'studs');
  
  // Data states
  const [cats, setCats] = useState([]);
  const [awards, setAwards] = useState({});
  const [instagramPhotos, setInstagramPhotos] = useState([]);
  
  // Loading states
  const [loadingCats, setLoadingCats] = useState(true);
  const [loadingAwards, setLoadingAwards] = useState(false);
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  
  // Error states
  const [catsError, setCatsError] = useState(null);
  const [awardsError, setAwardsError] = useState(null);
  const [photosError, setPhotosError] = useState(null);

  // Update active tab when URL changes
  useEffect(() => {
    if (urlTab) {
      setActiveTab(urlTab);
    }
  }, [urlTab]);

  // Fetch cats on component mount
  useEffect(() => {
    fetchCats();
  }, []);

  // Fetch data based on active tab
  useEffect(() => {
    if (activeTab === 'awards' && Object.keys(awards).length === 0) {
      fetchAwards();
    }
    if (activeTab === 'photos' && instagramPhotos.length === 0) {
      fetchInstagramPhotos();
    }
  }, [activeTab]);

  const fetchCats = async () => {
    setLoadingCats(true);
    setCatsError(null);
    
    try {
      const data = await catsAPI.getAll();
      setCats(data);
    } catch (error) {
      console.error('Error fetching cats:', error);
      setCatsError('Não foi possível carregar os gatos');
    } finally {
      setLoadingCats(false);
    }
  };

  const fetchAwards = async () => {
    setLoadingAwards(true);
    setAwardsError(null);
    
    try {
      const data = await awardsAPI.getAllGroupedByYear();
      setAwards(data);
    } catch (error) {
      console.error('Error fetching awards:', error);
      setAwardsError('Não foi possível carregar as premiações');
    } finally {
      setLoadingAwards(false);
    }
  };

  const fetchInstagramPhotos = async () => {
    setLoadingPhotos(true);
    setPhotosError(null);
    
    try {
      const data = await instagramAPI.getFeed();
      
      if (data.success) {
        setInstagramPhotos(data.data);
      } else {
        throw new Error('Failed to fetch Instagram photos');
      }
    } catch (error) {
      console.error('Error fetching Instagram feed:', error);
      setPhotosError('Não foi possível carregar as fotos do Instagram');
      // Fallback to mock photos
      setInstagramPhotos(mockPhotos.map((url, index) => ({
        id: `mock-${index}`,
        media_url: url,
        permalink: '#'
      })));
    } finally {
      setLoadingPhotos(false);
    }
  };

  // Filter cats by category
  const studCats = cats.filter(cat => cat.category === 'stud');
  const kittenCats = cats.filter(cat => cat.category === 'kitten');

  const tabs = [
    { id: 'studs', label: t.ourCats.studs },
    { id: 'kittens', label: t.ourCats.kittens },
    { id: 'awards', label: t.ourCats.awards },
    { id: 'photos', label: t.ourCats.photos }
  ];

  return (
    <div className="min-h-screen bg-white">
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-4xl sm:text-5xl font-semibold text-brown-600 mb-12 text-center">
            {t.ourCats.title}
          </h1>

          {/* Custom Tabs */}
          <div className="mb-12">
            <div className="border-b border-gray-200">
              <nav className="flex flex-wrap gap-4 -mb-px justify-center">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`pb-2 px-4 border-b-2 font-medium text-base transition-colors ${
                      activeTab === tab.id
                        ? 'border-brown-900 text-brown-900'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Studs */}
          {activeTab === 'studs' && (
            <>
              {loadingCats ? (
                <div className="flex justify-center items-center py-20">
                  <Loader2 className="animate-spin text-brown-900" size={40} />
                </div>
              ) : catsError ? (
                <div className="text-center py-10">
                  <p className="text-red-600 mb-4">{catsError}</p>
                  <button 
                    onClick={fetchCats}
                    className="px-6 py-2 bg-brown-900 text-white rounded-lg hover:bg-brown-800 transition-colors"
                  >
                    {language === 'pt' ? 'Tentar Novamente' : 'Try Again'}
                  </button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {studCats.map(cat => (
                      <CatCard key={cat.id} cat={cat} />
                    ))}
                  </div>

                  {/* Photographer Credit */}
                  <div className="mt-8 p-6 bg-brown-900 text-white rounded-lg text-center">
                    <p className="text-lg font-bold">
                      {language === 'pt' 
                        ? <>Agradecemos, especialmente, à incrível fotógrafa <a href="https://www.instagram.com/carla_durante_pets" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-200 transition-colors">Carla Durante</a>, pelo trabalho magnífico no registro dos gatos ocicats no Brasil.</>
                        : <>We especially thank the incredible photographer <a href="https://www.instagram.com/carla_durante_pets" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-200 transition-colors">Carla Durante</a>, for the magnificent work in capturing the Ocicat cats in Brazil.</>
                      }
                    </p>
                  </div>
                </>
              )}
            </>
          )}

          {/* Kittens */}
          {activeTab === 'kittens' && (
            <>
              {loadingCats ? (
                <div className="flex justify-center items-center py-20">
                  <Loader2 className="animate-spin text-brown-900" size={40} />
                </div>
              ) : catsError ? (
                <div className="text-center py-10">
                  <p className="text-red-600 mb-4">{catsError}</p>
                  <button 
                    onClick={fetchCats}
                    className="px-6 py-2 bg-brown-900 text-white rounded-lg hover:bg-brown-800 transition-colors"
                  >
                    {language === 'pt' ? 'Tentar Novamente' : 'Try Again'}
                  </button>
                </div>
              ) : kittenCats.length === 0 ? (
                <div className="text-center py-20">
                  <p className="text-gray-600 text-lg">
                    {language === 'pt' 
                      ? 'Nenhum filhote disponível no momento.' 
                      : 'No kittens available at the moment.'}
                  </p>
                </div>
              ) : (
                <>
                  {/* Subtitle with update date */}
                  <div className="mb-8 text-center">
                    <h2 className="text-2xl font-semibold text-brown-900">
                      {language === 'pt' ? 'Filhotes Disponíveis' : 'Available Kittens'}
                    </h2>
                    <p className="text-gray-600 mt-2">
                      {language === 'pt' ? '(atualizado em 26/07/26)' : '(updated on 07/26/26)'}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {kittenCats.map(cat => (
                      <CatCard key={cat.id} cat={cat} />
                    ))}
                  </div>

                  {/* Contact Banner */}
                  <div className="mt-8 p-8 bg-gradient-to-r from-brown-900 to-brown-800 text-white rounded-lg text-center shadow-xl">
                    <h3 className="text-2xl font-bold mb-4">
                      {language === 'pt' 
                        ? 'Entre em contato para informações mais atualizadas' 
                        : 'Contact us for updated information'}
                    </h3>
                    <button
                      onClick={() => navigate('/contact')}
                      className="px-8 py-3 bg-white text-brown-900 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg"
                    >
                      {language === 'pt' ? 'Ir para Contato' : 'Go to Contact'}
                    </button>
                  </div>

                  {/* Photographer Credit */}
                  <div className="mt-8 p-6 bg-brown-900 text-white rounded-lg text-center">
                    <p className="text-lg font-bold">
                      {language === 'pt' 
                        ? <>Agradecemos, especialmente, à incrível fotógrafa <a href="https://www.instagram.com/carla_durante_pets" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-200 transition-colors">Carla Durante</a>, pelo trabalho magnífico no registro dos gatos ocicats no Brasil.</>
                        : <>We especially thank the incredible photographer <a href="https://www.instagram.com/carla_durante_pets" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-200 transition-colors">Carla Durante</a>, for the magnificent work in capturing the Ocicat cats in Brazil.</>
                      }
                    </p>
                  </div>

                  {/* CTA Section - How to have an Ocicat */}
                  <div className="mt-16 py-16 px-8 bg-brown-900 text-white rounded-2xl text-center">
                    <h2 className="text-4xl font-bold mb-6">
                      {language === 'pt' ? 'Como ter um Ocicat?' : 'How to have an Ocicat?'}
                    </h2>
                    <p className="text-xl mb-8 text-gray-200">
                      {language === 'pt' 
                        ? 'Descubra como você pode fazer parte da família Ocicat Brasil'
                        : 'Discover how you can become part of the Ocicat Brasil family'}
                    </p>
                    <button
                      onClick={() => {
                        navigate('/cattery?tab=how');
                        setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
                      }}
                      className="inline-block px-10 py-4 bg-white text-brown-900 rounded-full hover:bg-gray-100 transition-all duration-200 text-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105"
                    >
                      {language === 'pt' ? 'Saiba Mais' : 'Learn More'}
                    </button>
                  </div>
                </>
              )}
            </>
          )}

          {/* Awards */}
          {activeTab === 'awards' && (
            <>
              {loadingAwards ? (
                <div className="flex justify-center items-center py-20">
                  <Loader2 className="animate-spin text-brown-900" size={40} />
                </div>
              ) : awardsError ? (
                <div className="text-center py-10">
                  <p className="text-red-600 mb-4">{awardsError}</p>
                  <button 
                    onClick={fetchAwards}
                    className="px-6 py-2 bg-brown-900 text-white rounded-lg hover:bg-brown-800 transition-colors"
                  >
                    {language === 'pt' ? 'Tentar Novamente' : 'Try Again'}
                  </button>
                </div>
              ) : (
                <div className="space-y-12">
                  {Object.keys(awards).sort().reverse().map(year => (
                    <div key={year}>
                      {/* Year Header */}
                      <h2 className="text-3xl font-bold text-brown-600 mb-6">{year}</h2>
                      
                      {/* Cats Grid for this year */}
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {awards[year].map((cat, index) => (
                          <Card key={index} className="hover:shadow-lg transition-shadow overflow-hidden">
                            {/* Cat Photo */}
                            <div className="relative w-full h-64 overflow-hidden">
                              <img
                                loading="lazy" decoding="async" src={cat.image}
                                alt={cat.catName}
                                className="w-full h-full object-cover"
                              />
                            </div>
                            
                            <CardHeader>
                              <CardTitle className="text-xl text-brown-900">{cat.catName}</CardTitle>
                              <CardDescription className="text-sm text-gray-600">
                                ({cat.registeredName})
                              </CardDescription>
                            </CardHeader>
                            
                            <CardContent>
                              <div className="space-y-2">
                                {cat.awards.map((award, awardIndex) => (
                                  <div key={awardIndex} className="flex items-start gap-2">
                                    <Award className="text-yellow-600 mt-0.5 flex-shrink-0" size={18} />
                                    <span className="text-gray-700">{award}</span>
                                  </div>
                                ))}
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </div>
                  ))}

                  {/* Awards Context Text */}
                  <div className="mt-12 p-6 bg-gray-50 rounded-lg border-l-4 border-brown-900">
                    <p className="text-gray-700 leading-relaxed text-lg">
                      {language === 'pt' 
                        ? 'Os prêmios alcançados foram conquistados em disputas com os melhores gatos de diversas raças como: Bengal, British, Burmês. Todos foram concedidos pela FFB-FIFe. As avaliações dos ocicats foram realizadas, exclusivamente, por juízes internacionais - nenhum juiz brasileiro é apto para julgar/avaliar a raça ocicat.'
                        : 'The awards achieved were won in competitions with the best cats of various breeds such as: Bengal, British, Burmese. All were granted by FFB-FIFe. The evaluations of the ocicats were performed exclusively by international judges - no Brazilian judge is qualified to judge/evaluate the ocicat breed.'}
                    </p>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Photos */}
          {activeTab === 'photos' && (
            <div>
              <div className="mb-6 text-center">
                <a 
                  href="https://www.instagram.com/ocicat.brasil" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-2 text-brown-900 hover:text-brown-700 transition-colors"
                >
                  <Camera size={20} />
                  <span className="font-medium">@ocicat.brasil</span>
                </a>
              </div>

              {loadingPhotos && (
                <div className="flex justify-center items-center py-20">
                  <Loader2 className="animate-spin text-brown-900" size={40} />
                </div>
              )}

              {photosError && (
                <div className="text-center py-10">
                  <p className="text-red-600 mb-4">{photosError}</p>
                  <button 
                    onClick={fetchInstagramPhotos}
                    className="px-6 py-2 bg-brown-900 text-white rounded-lg hover:bg-brown-800 transition-colors"
                  >
                    {language === 'pt' ? 'Tentar Novamente' : 'Try Again'}
                  </button>
                </div>
              )}

              {!loadingPhotos && !photosError && (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {instagramPhotos.map((photo) => (
                      <a
                        key={photo.id}
                        href={photo.permalink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="aspect-square overflow-hidden rounded-lg hover:scale-105 transition-transform duration-300 shadow-md hover:shadow-xl"
                      >
                        <img
                          src={photo.media_type === 'VIDEO' ? (photo.thumbnail_url || photo.media_url) : photo.media_url}
                          alt={photo.caption || 'Ocicat'}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </a>
                    ))}
                  </div>

                  {/* Photographer Credit */}
                  <div className="mt-8 p-6 bg-brown-900 text-white rounded-lg text-center">
                    <p className="text-lg font-bold">
                      {language === 'pt' 
                        ? <>Agradecemos, especialmente, à incrível fotógrafa <a href="https://www.instagram.com/carla_durante_pets" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-200 transition-colors">Carla Durante</a>, pelo trabalho magnífico no registro dos gatos ocicats no Brasil.</>
                        : <>We especially thank the incredible photographer <a href="https://www.instagram.com/carla_durante_pets" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-200 transition-colors">Carla Durante</a>, for the magnificent work in capturing the Ocicat cats in Brazil.</>
                      }
                    </p>
                  </div>
                </>
              )}
            </div>
          )}

        </div>
      </section>

      <Footer />
    </div>
  );
};